import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  Invoice,
  InvoiceStatusEnum,
  OutstandingInvoice,
  CreatePaymentInput,
  InitiatePaymentInput,
  InitiatePaymentResult,
  InitiateManualEftResult,
  Payment
} from './types/invoices.types';

export interface ListInvoicesParams {
  /** Filter by customer ID */
  customerId?: number
  /** Filter by invoice status */
  status?: InvoiceStatusEnum
  /** Max records per page */
  limit?: number
  /** Number of records to skip */
  offset?: number
}

export interface ListMyInvoicesParams {
  limit?: number
  offset?: number
  // Scopes to one booking (joined through the invoice's quote, since
  // invoices carry no enquiry_id of their own).
  enquiryId?: number
}

function buildMyInvoicesQuery(params: ListMyInvoicesParams): string {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  if (params.enquiryId !== undefined) {

    query.set('enquiryId', String(params.enquiryId));
  }

  return query.size > 0 ? `?${ query.toString() }` : '';
}

/** Self-service. The caller's own invoices - empty if they have no
 * customer record yet, not an error. */
export async function getMyInvoices(
  params: ListMyInvoicesParams = {},
  token: string
): Promise<PaginatedResponse<Invoice>> {

  return apiRequest<PaginatedResponse<Invoice>>(`/invoices/me${ buildMyInvoicesQuery(params) }`, undefined, token);
}

/** Self-service. Own invoices plus every student who has accepted the
 * caller as a linked payer. */
export async function getPayableByMeInvoices(
  params: ListMyInvoicesParams = {},
  token: string
): Promise<PaginatedResponse<Invoice>> {

  return apiRequest<PaginatedResponse<Invoice>>(`/invoices/payable-by-me${ buildMyInvoicesQuery(params) }`, undefined, token);
}

/** Self-service. Every invoice the caller still owes money on, keyed by the
 * booking it belongs to.
 *
 * One call for a whole list of bookings, joined to the rows client-side by
 * enquiryId - the alternative was an invoice request per row. Not
 * paginated: the endpoint caps itself and a customer with that many unpaid
 * invoices is a collections problem, not a paging one. */
export async function getMyOutstandingInvoices(
  token: string
): Promise<ApiResponse<OutstandingInvoice[]>> {

  return apiRequest<ApiResponse<OutstandingInvoice[]>>('/invoices/me/outstanding', undefined, token);
}

/** Self-service. A single invoice, scoped to the caller's own or an
 * accepted-linked-payer's access - 403s otherwise. */
export async function getMyInvoiceById(
  id: number,
  token: string
): Promise<ApiResponse<Invoice>> {

  return apiRequest<ApiResponse<Invoice>>(`/invoices/me/${ id }`, undefined, token);
}

/** Self-service counterpart to initiatePayment - authenticated instead of
 * admin, same authorization scope as getMyInvoiceById. Returns a redirect
 * URL. */
export async function payMyInvoice(
  id: number,
  input: InitiatePaymentInput,
  token: string
): Promise<ApiResponse<InitiatePaymentResult>> {

  return apiRequest<ApiResponse<InitiatePaymentResult>>(
    `/invoices/me/${ id }/pay`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

/** Admin only. Returns a paginated, optionally filtered list of invoices. */
export async function listInvoices(
  params: ListInvoicesParams = {},
  token: string
): Promise<PaginatedResponse<Invoice>> {

  const query = new URLSearchParams();

  if (params.customerId !== undefined) {

    query.set('customerId', String(params.customerId));
  }

  if (params.status !== undefined) {

    query.set('status', params.status);
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Invoice>>(`/invoices${ qs }`, undefined, token);
}

/** Admin only. Retrieves a single invoice by primary key. */
export async function getInvoiceById(
  id: number,
  token: string
): Promise<ApiResponse<Invoice>> {

  return apiRequest<ApiResponse<Invoice>>(`/invoices/${ id }`, undefined, token);
}

/** Admin only. Sends a DRAFT invoice (transitions it to SENT). */
export async function sendInvoice(
  id: number,
  token: string
): Promise<ApiResponse<Invoice>> {

  return apiRequest<ApiResponse<Invoice>>(
    `/invoices/${ id }/send`,
    {
      method: 'POST'
    },
    token
  );
}

/** Admin only. Voids an invoice. */
export async function voidInvoice(
  id: number,
  token: string
): Promise<ApiResponse<Invoice>> {

  return apiRequest<ApiResponse<Invoice>>(
    `/invoices/${ id }/void`,
    {
      method: 'POST'
    },
    token
  );
}

/** Admin only. Records a payment against an invoice. */
export async function recordPayment(
  id: number,
  input: Omit<CreatePaymentInput, 'invoiceId'>,
  token: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(
    '/payments',
    {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        invoiceId: id
      })
    },
    token
  );
}

/** Initiates a gateway payment for a SENT or PARTIAL invoice. Returns a redirect URL. */
export async function initiatePayment(
  id: number,
  input: InitiatePaymentInput,
  token: string
): Promise<ApiResponse<InitiatePaymentResult>> {

  return apiRequest<ApiResponse<InitiatePaymentResult>>(
    `/invoices/${ id }/initiate-payment`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

/** Creates a manual EFT payment for the invoice's outstanding balance. No gateway - returns a bank reference. */
export async function initiateManualEft(
  id: number,
  token: string
): Promise<ApiResponse<InitiateManualEftResult>> {

  return apiRequest<ApiResponse<InitiateManualEftResult>>(
    `/invoices/${ id }/initiate-eft`,
    {
      method: 'POST'
    },
    token
  );
}
